import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import { useNavigate } from 'react-router';
import { maps as mapsAPI, stroot } from '@birdi/js-sdk';
import type { ProcessingOverview as ProcessingOverviewType } from '@birdi/js-sdk/src/public/maps';
import { useMetaSelector } from '../../meta/reducer';
import { posthog } from '../../lib/posthog';
import MapSVG from '@birdi/icons/map.svg';
import type { WidgetProps } from '../tabs';
import { ReorderingIcon } from './reordering-icon';

const OVERVIEWS_PER_PAGE = 3;
// uat path trigger

export const ProcessingOverview: React.FC<WidgetProps> = ({
  isReorderMode,
  isHovered,
  isDragging,
  suppressIntercomAnchor,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const meta = useMetaSelector();

  const [overviews, setOverviews] = useState<ProcessingOverviewType[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const workspaceId = meta.workspace?.id || meta.organisation.id;

  const fetchOverviews = async (limit: number) => {
    if (!meta.organisation?.id || !workspaceId) {
      return { overviews: [], hasMore: false };
    }
    // Build options
    const opts: { limit: number } = {
      limit: limit + 1,
    };

    // Fetch one extra to check if there are more
    const res = await mapsAPI.getProcessingOverview(
      stroot('provw1'),
      workspaceId,
      opts,
    );
    const fetchedOverviews = res.body || [];
    const hasMoreItems = fetchedOverviews.length > limit;
    return {
      overviews: hasMoreItems ? fetchedOverviews.slice(0, limit) : fetchedOverviews,
      hasMore: hasMoreItems,
    };
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const result = await fetchOverviews(OVERVIEWS_PER_PAGE);
        setOverviews(result.overviews);
        setHasMore(result.hasMore);
      } catch (e) {
        console.error('Error fetching processing overview:', e);
        setOverviews([]);
        setHasMore(false);
      }
      setLoading(false);
    })();
  }, [meta.organisation?.id, workspaceId]);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    posthog?.capture('dashboard processing overview expand clicked');

    try {
      const newLimit = overviews.length + OVERVIEWS_PER_PAGE;
      const result = await fetchOverviews(newLimit);
      setOverviews(result.overviews);
      setHasMore(result.hasMore);
    } catch (e) {
      console.error('Error loading more overviews:', e);
    }
    setLoadingMore(false);
  };

  const overviewsWithMetrics = overviews.filter(
    (o) =>
      o.imagesCount !== undefined ||
      o.groundResolution ||
      o.flightAltitude ||
      o.coverageArea ||
      o.reprojectionError,
  );

  const isExpandDisabled =
    overviews.length === 0 || !hasMore || loading || loadingMore;

  const handleOverviewClick = (overview: ProcessingOverviewType) => {
    posthog?.capture('dashboard processing overview clicked', {
      overviewId: overview.id,
      mapId: overview.mapId,
    });
    // Navigate to the map
    navigate(`/map/next/${overview.mapId}`);
  };

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        border-radius: 1em;
        box-shadow: 2px 2px 2px ${theme.mono30};
        background: ${theme.mono0};
        min-height: 320px;
        min-width: 0;
        max-width: 100%;
        @media (max-width: ${breakpoints.s}) {
          min-height: 220px;
        }
        @media (max-width: ${breakpoints.xs}) {
          min-height: 180px;
          width: 100%;
        }
      `}
    >
      <div
        css={css`
          color: ${theme.midnight};
          font-weight: 700;
          background: ${theme.lightOcean};
          padding: 1em;
          border-top-left-radius: 1em;
          border-top-right-radius: 1em;
          display: flex;
          align-items: center;
          gap: 0.5em;
        `}
        id={suppressIntercomAnchor ? undefined : 'processing-overview'}
      >
        <ReorderingIcon
          isReorderMode={isReorderMode}
          isHovered={isHovered}
          isDragging={isDragging}
        />
        <span>Processing overview</span>
      </div>
      <div
        css={css`
          flex: 1 1 auto;
          padding: 1em;
          color: ${theme.mono50};
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
        `}
      >
        <div
          css={css`
            margin-bottom: 1em;
          `}
        >
          <span>A high-level summary of the processing accuracy</span>
        </div>

        {/* Empty state */}
        {!loading && overviewsWithMetrics.length === 0 && (
          <div
            css={css`
              height: 13em;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
            `}
          >
            <div>
              <strong>Your processing accuracy summary will appear here.</strong>
            </div>
            <div
              css={css`
                margin-top: 0.5em;
              `}
            >
              Run or complete map processing to see a high-level overview of accuracy results in this space.
            </div>
          </div>
        )}

        {/* Overviews list */}
        {!loading && overviewsWithMetrics.length > 0 && (
          <div>
            {overviewsWithMetrics.map((overview) => {
              const previewUrl = overview.previewUrl;

              return (
                <div
                  key={overview.id}
                  onClick={() => handleOverviewClick(overview)}
                  css={css`
                    display: flex;
                    gap: 2em;
                    background: ${theme.mono0};
                    border-bottom: 1px solid ${theme.mono30};
                    padding: 1em;
                    cursor: pointer;
                    transition: background 0.15s;
                    min-width: 0;
                    &:hover {
                      background: ${theme.mono20};
                    }
                    &:last-child {
                      border-bottom: none;
                    }
                    @media (max-width: ${breakpoints.xs}) {
                      flex-direction: column;
                      gap: 1em;
                    }
                  `}
                >
                  {/* Column 1: Thumbnail (fixed size so all rows match) */}
                  <div
                    css={css`
                      min-width: 120px;
                      width: 200px;
                      height: 170px;
                      flex-shrink: 0;
                      background: ${theme.mono20};
                      border-radius: 6px;
                      overflow: hidden;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      @media (max-width: ${breakpoints.xs}) {
                        width: 100%;
                        max-width: 200px;
                        height: 120px;
                      }
                    `}
                  >
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="Processing preview"
                        css={css`
                          width: 100%;
                          height: 100%;
                          object-fit: cover;
                        `}
                      />
                    ) : null}
                  </div>

                  {/* Columns 2 & 3: Titles (bold) + Values, then map name spanning both */}
                  <div
                    css={css`
                      flex: 1;
                      min-width: 0;
                      display: flex;
                      flex-direction: column;
                      gap: 0.25em;
                    `}
                  >
                    <div
                      css={css`
                        display: grid;
                        grid-template-columns: 1fr auto;
                        gap: 0.25em 0.75em;
                        align-items: baseline;
                        font-size: 0.9em;
                        color: ${theme.midnight};
                        text-align: left;
                      `}
                    >
                      {overview.imagesCount !== undefined && (
                        <>
                          <div
                            css={css`
                              font-weight: 700;
                            `}
                          >
                            Images:
                          </div>
                          <div>
                            {overview.imagesCount}
                          </div>
                        </>
                      )}
                      {overview.groundResolution && (
                        <>
                          <div
                            css={css`
                              font-weight: 700;
                            `}
                          >
                            Ground resolution:
                          </div>
                          <div>
                            {overview.groundResolution}
                          </div>
                        </>
                      )}
                      {overview.flightAltitude && (
                        <>
                          <div
                            css={css`
                              font-weight: 700;
                            `}
                          >
                            Flight altitude:
                          </div>
                          <div>
                            {overview.flightAltitude}
                          </div>
                        </>
                      )}
                      {overview.coverageArea && (
                        <>
                          <div
                            css={css`
                              font-weight: 700;
                            `}
                          >
                            Coverage area:
                          </div>
                          <div>
                            {overview.coverageArea}
                          </div>
                        </>
                      )}
                      {overview.reprojectionError && (
                        <>
                          <div
                            css={css`
                              font-weight: 700;
                            `}
                          >
                            Reprojection error:
                          </div>
                          <div>
                            {overview.reprojectionError}
                          </div>
                        </>
                      )}
                    </div>

                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: 0.5em;
                        color: ${theme.mono50};
                        font-size: 0.9em;
                        margin-top: 0.5em;
                      `}
                    >
                      <span
                        css={css`
                          display: flex;
                          width: 14px;
                          height: 14px;
                        `}
                      >
                        <MapSVG />
                      </span>
                      <span>{overview.mapName || 'Untitled Map'}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {loading && (
          <div
            css={css`
              text-align: center;
              margin: 1em 0;
            `}
          >
            Loading...
          </div>
        )}
      </div>

      <div
        css={css`
          padding: 1em;
        `}
      >
        <button
          onClick={handleLoadMore}
          disabled={isExpandDisabled}
          css={css`
            width: 100%;
            color: ${isExpandDisabled ? theme.mono40 : theme.mono60};
            border: solid 1px ${theme.mono30};
            border-radius: 5px;
            padding: 0.5em;
            font-weight: 500;
            cursor: ${isExpandDisabled ? 'not-allowed' : 'pointer'};
            background: ${theme.mono0};
            opacity: ${isExpandDisabled ? 0.6 : 1};
          `}
        >
          {loadingMore ? 'Loading...' : 'Expand'}
        </button>
      </div>
    </div>
  );
};

